<template>
  <div class="p-6">
    <!-- Status Ribbon Removed -->

    <!-- Header Section -->
    <div class="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-4">
      <!-- Left Group: Title & Refresh & Mobile Stats -->
      <div class="flex items-center justify-between">
          <!-- Sub-group for Title & Refresh -->
          <div class="flex items-center space-x-3">
            <h1 class="text-3xl font-bold text-black">Screens</h1>
            <button
              @click="fetchScreensData"
              :disabled="loading"
              title="Refresh Data"
              class="p-1.5 rounded-full text-gray-600 hover:bg-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <!-- Refresh Icon SVG -->
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
          <!-- Stats Tags (Mobile Only) -->
          <div class="flex items-center space-x-3 md:hidden">
            <span class="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-green-200 text-green-800">
              <svg class="-ml-0.5 mr-1 h-2.5 w-2.5 text-green-500" fill="currentColor" viewBox="0 0 8 8">
                <circle cx="4" cy="4" r="3" />
              </svg>
              {{ onlineCount }}
              <!-- Text hidden on mobile anyway by parent, but kept for consistency -->
              <span class="hidden sm:inline ml-1">Online</span>
            </span>
            <span class="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-red-200 text-red-800">
              <svg class="-ml-0.5 mr-1 h-2.5 w-2.5 text-red-500" fill="currentColor" viewBox="0 0 8 8">
                <circle cx="4" cy="4" r="3" />
              </svg>
              {{ offlineCount }}
              <span class="hidden sm:inline ml-1">Offline</span>
            </span>
          </div>
      </div>

      <!-- Right Group: Search & Desktop Stats -->
      <div class="flex flex-col md:flex-row items-stretch md:items-center md:space-x-4">
         <!-- Bottom Row: Search & Sort Controls -->
         <div class="flex items-center gap-4">
           <!-- Search Input -->
           <div class="relative flex-grow">
             <input
               type="text"
               v-model="searchQuery"
               placeholder="Search..."
               class="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full md:w-72"
             />
             <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                 <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
               </svg>
             </div>
           </div>
           <!-- Sort Controls (Mobile Only) -->
           <div class="relative md:hidden flex-shrink-0">
               <!-- Custom Sort Button -->
               <button
                 @click="isSortMenuOpen = !isSortMenuOpen"
                 class="flex items-center justify-between w-full text-sm border border-primary bg-primary text-white hover:bg-primary-600 rounded-full focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary py-1 px-3"
               >
                  <span>{{ currentSortOptionText }}</span>
                   <!-- Direction Indicator Icon -->
                   <svg v-if="sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 ml-1 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
               </button>
               <!-- Dropdown Menu with Transition -->
               <transition name="sort-menu-fade">
                 <div v-if="isSortMenuOpen" class="absolute right-0 mt-2 w-36 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20 py-1">
                     <a v-for="option in sortOptions"
                        :key="option.value"
                        href="#"
                        @click.prevent="selectSortOption(option.value)"
                        class="flex justify-between items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        :class="{ 'font-semibold bg-gray-50': sortColumn === option.value }"
                     >
                         <span>{{ option.text }}</span>
                         <!-- Conditional Sort Direction Icon -->
                         <span v-if="sortColumn === option.value">
                            <svg v-if="sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                             </svg>
                             <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                             </svg>
                         </span>
                     </a>
                 </div>
               </transition>
           </div>
         </div>

         <!-- Stats Tags (Desktop Only) -->
         <div class="hidden md:flex items-center space-x-3 self-end md:self-center">
            <span class="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-green-200 text-green-800">
              <!-- Online Icon SVG -->
              <svg class="-ml-0.5 mr-1 h-2.5 w-2.5 text-green-500" fill="currentColor" viewBox="0 0 8 8">
                 <circle cx="4" cy="4" r="3" />
               </svg>
              {{ onlineCount }}
              <span class="hidden sm:inline ml-1">Online</span>
            </span>
            <span class="inline-flex items-center px-2 py-1 rounded-full text-sm font-medium bg-red-200 text-red-800">
               <!-- Offline Icon SVG -->
               <svg class="-ml-0.5 mr-1 h-2.5 w-2.5 text-red-500" fill="currentColor" viewBox="0 0 8 8">
                 <circle cx="4" cy="4" r="3" />
               </svg>
              {{ offlineCount }}
              <span class="hidden sm:inline ml-1">Offline</span>
            </span>
          </div>
       </div>
    </div>

    <!-- Error message if the API request fails -->
    <div v-if="apiError" class="text-center text-red-600">
      Error: Please refresh to retry.
    </div>

    <!-- Desktop Table View (Hidden on Mobile) -->
    <div class="hidden md:block">
      <!-- Loading State for Desktop -->
      <div v-if="loading" class="text-center py-20 flex justify-center items-center">
        <svg class="animate-spin h-12 w-12 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>

      <!-- Error State for Desktop -->
      <div v-else-if="apiError" class="text-center py-20 text-red-600">
        <p class="text-lg">Error loading screen data. Please refresh to try again.</p>
      </div>

      <!-- Table Container - Only show if not loading and no error -->
      <div v-if="!loading && !apiError" class="table-container">
        <table class="min-w-full table-auto">
          <thead>
              <tr class="bg-gray-50">
                <!-- Screen Name (Sortable) -->
                <th class="py-3 px-3 text-left text-gray-600 font-semibold border-r border-gray-200">
                    <button type="button" @click="sortBy('name')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- Identification Icon -->
                            <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                        </svg>
                        <span class="text-sm">Name</span>
                        <!-- Sort Icons -->
                        <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'name' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'name' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
                </th>
                <!-- Screen Serial (Sortable) -->
                <th class="py-3 px-3 text-left text-gray-600 font-semibold border-r border-gray-200">
                    <button type="button" @click="sortBy('screen_id')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- Hashtag Icon -->
                            <path stroke-linecap="round" stroke-linejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                        </svg>
                        <span class="text-sm">Serial</span>
                        <!-- Sort Icons -->
                        <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'screen_id' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'screen_id' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
                </th>
                <!-- Screen Group (Sortable) -->
                <th class="py-3 px-3 text-left text-gray-600 font-semibold border-r border-gray-200">
                    <button type="button" @click="sortBy('group_name')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- Users Icon -->
                            <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span class="text-sm">Group</span>
                        <!-- Sort Icons -->
                        <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'group_name' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'group_name' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
                </th>
                <!-- Cruiser (Sortable) -->
                <th class="py-3 px-3 text-left text-gray-600 font-semibold border-r border-gray-200">
                    <button type="button" @click="sortBy('user_name')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- User Icon -->
                             <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span class="text-sm">Cruiser</span>
                        <!-- Sort Icons -->
                        <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'user_name' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'user_name' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
                </th>
                <!-- User Mobile (Sortable) -->
                <th class="py-3 px-3 text-left text-gray-600 font-semibold border-r border-gray-200">
                    <button type="button" @click="sortBy('mobile')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- Device Phone Mobile Icon -->
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span class="text-sm">Mobile</span>
                        <!-- Sort Icons -->
                        <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'mobile' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'mobile' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
                </th>
                <!-- Plate Number (Sortable) -->
                <th class="py-3 px-3 text-left text-gray-600 font-semibold border-r border-gray-200">
                      <button type="button" @click="sortBy('plate_number')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                         <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- Identification Icon -->
                            <path stroke-linecap="round" stroke-linejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                        </svg>
                        <span class="text-sm">Plate</span>
                        <!-- Sort Icons -->
                        <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'plate_number' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'plate_number' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
                </th>
                <!-- Screen Status (Sortable) -->
                <th class="py-3 px-3 text-left text-gray-600 font-semibold border-r border-gray-200">
                    <button type="button" @click="sortBy('online_status')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- Wifi Icon -->
                            <path stroke-linecap="round" stroke-linejoin="round" d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20.25a.75.75 0 01.75-.75v-.611a5.5 5.5 0 01-1.5 0v.611a.75.75 0 01-.75.75gM2.25 13.5c.64-.64 1.493-.989 2.476-1.169v-.39a2.25 2.25 0 014.5 0v.39c.985.18 1.838.528 2.476 1.169M19.5 13.5c-.64-.64-1.493-.989-2.476-1.169v-.39a2.25 2.25 0 00-4.5 0v.39c-.985.18-1.838.528-2.476 1.169M12 11.25a.75.75 0 01.75-.75v-.611a2.25 2.25 0 01-1.5 0v.611a.75.75 0 01.75.75z" />
                        </svg>
                        <span class="text-sm">Status</span>
                        <!-- Sort Icons -->
                         <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'online_status' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'online_status' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
                </th>
                <!-- Daily Online Time (Sortable) -->
                <th class="py-3 px-3 text-left text-gray-600 font-semibold border-r border-gray-200">
                       <button type="button" @click="sortBy('daily_online_time')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- Clock Icon -->
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span class="text-sm">Online</span>
                        <!-- Sort Icons -->
                         <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'daily_online_time' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'daily_online_time' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
                </th>
                <!-- Last Seen (Sortable) -->
                <th class="py-3 px-3 text-left text-gray-600 font-semibold last:border-r-0">
                    <button type="button" @click="sortBy('last_offline')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                            <!-- Calendar Icon -->
                            <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span class="text-sm">Last Seen</span>
                        <!-- Sort Icons -->
                         <span class="opacity-50 group-hover:opacity-100">
                           <svg v-if="sortColumn === 'last_offline' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
                           </svg>
                           <svg v-else-if="sortColumn === 'last_offline' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                           </svg>
                           <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                               <path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                           </svg>
                        </span>
                    </button>
                </th>
            </tr>
          </thead>
          <tbody>
            <tr
                v-for="(screen, index) in sortedScreens"
              :key="index"
                class="bg-white text-sm hover:bg-gray-50"
              >
                <!-- 1. Screen Name -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0 capitalize">{{ screen.name }}</td>
                <!-- 2. Screen Serial -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0 relative group cursor-pointer" @click="copyToClipboard(screen.screen_id, screen.screen_id)" title="Click to copy">
                    <!-- Original text span - Truncated -->
                    <span class="block w-full truncate relative group-hover:![mask-image:none] [mask-image:linear-gradient(to_right,black_20%,transparent)]">
                        <span class="inline-block font-mono opacity-75">
                            {{ truncateSerial(screen.screen_id) }}
                        </span>
                    </span>
                    <!-- Centered Absolutely positioned span shown only on hover - Full Text -->
                    <span class="absolute z-20 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-teal-100 px-2 py-0.5 rounded shadow whitespace-nowrap opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 pointer-events-none group-hover:pointer-events-auto transition-all duration-150 ease-in-out">
                        {{ screen.screen_id }} <!-- Show full serial here -->
                    </span>
                    <!-- Copied Notification -->
                    <transition name="fade">
                         <span v-if="copiedItemId === screen.screen_id" class="absolute -top-1 -right-1 bg-teal-200 text-teal-900 text-[10px] font-bold px-1 py-0 rounded-full shadow-md z-40">
                             Copied!
                         </span>
                    </transition>
                </td>
                <!-- 3. Screen Group -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0">
                    <span :class="['inline-block', 'px-2', 'py-0.5', 'rounded', 'text-xs', getGroupColor(screen.group_name)]">
                        {{ screen.group_name?.replace(/dkilo /i, '') }}
                    </span>
                </td>
                <!-- 4. User Name -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0">
                     <span class="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700">{{ formatUserName(screen.user_name) }}</span>
                </td>
                <!-- 5. User Mobile -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0 relative group cursor-pointer" @click="copyToClipboard(screen.mobile, screen.mobile)" title="Click to copy">
                     <span class="inline-block group-hover:hidden">
                        {{ screen.mobile }}
                     </span>
                     <!-- Centered Absolutely positioned span shown only on hover -->
                     <span class="absolute z-20 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-teal-100 px-2 py-0.5 rounded shadow whitespace-nowrap opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 pointer-events-none group-hover:pointer-events-auto transition-all duration-150 ease-in-out">
                         {{ screen.mobile }}
                     </span>
                     <!-- Copied Notification -->
                    <transition name="fade">
                         <span v-if="copiedItemId === screen.mobile" class="absolute -top-1 -right-1 bg-teal-200 text-teal-900 text-[10px] font-bold px-1 py-0 rounded-full shadow-md z-40">
                             Copied!
                         </span>
                    </transition>
                </td>
                <!-- 6. Plate Number -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0">
                    <span class="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">{{ formatPlateNumber(screen.plate_number) }}</span>
                </td>
                <!-- 7. Screen Status -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0">
                     <span :class="['inline-block', 'px-2', 'py-0.5', 'rounded-full', 'text-xs', 'font-medium', screen.online_status?.toLowerCase() === 'online' ? 'bg-green-200 text-green-900' : 'bg-gray-100 text-gray-600']">{{ screen.online_status }}</span>
                </td>
                <!-- 8. Daily Online Time -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0">{{ formatTime(screen.daily_online_time) }}</td>
                <!-- 9. Last Offline -->
                <td class="py-3 px-4 border-b border-gray-200 border-r last:border-r-0">{{ formatRelativeTime(screen.last_offline) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Mobile Card View (Visible only on Mobile) -->
    <div class="block md:hidden py-2">
      <div v-if="loading" class="text-center py-10 flex justify-center items-center">
        <!-- Teal Loading Spinner -->
        <svg class="animate-spin h-10 w-10 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
      <div v-else-if="apiError" class="text-center py-10 text-red-500">
        <p>Error loading screens. Please try again later.</p>
      </div>
      <div v-else-if="!sortedScreens.length" class="text-center py-10 text-gray-500">
        <p>No screens found.</p>
      </div>
      <div v-else v-for="screen in sortedScreens" :key="screen.id || screen.screen_id" class="bg-white rounded-lg shadow p-4 mb-4">
        <!-- Card Header: Title and Status -->
        <div class="flex justify-between items-start mb-3">
          <h3 class="font-semibold text-lg text-gray-800 mr-2 capitalize" :title="screen.name">{{ screen.name || 'N/A' }}</h3>
          <span
            :class="[
              'px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap',
              screen.online_status?.toLowerCase() === 'online' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            ]"
          >
            {{ screen.online_status || 'Offline' }}
          </span>
        </div>

        <!-- Serial & Group Row -->
        <div class="grid grid-cols-2 gap-4 mb-3 pb-3 border-b border-gray-100">
          <!-- Column 1: Serial -->
          <div>
            <span class="text-sm font-medium text-gray-500 flex items-center mb-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                 <path stroke-linecap="round" stroke-linejoin="round" d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
               </svg>
              Serial
            </span>
            <div class="flex items-center justify-between">
              <span class="text-gray-800 font-mono text-sm truncate mr-2" :title="screen.screen_id">{{ screen.screen_id || 'N/A' }}</span>
              <button
                @click="copyToClipboard(screen.screen_id, screen.screen_id + '-serial-btn')"
                :title="copiedItemId === screen.screen_id + '-serial-btn' ? 'Copied!' : 'Copy Serial'"
                class="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
              >
                <svg v-if="copiedItemId === screen.screen_id + '-serial-btn'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </button>
            </div>
          </div>
          <!-- Column 2: Group -->
          <div>
             <span class="text-sm font-medium text-gray-500 flex items-center mb-1">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                 <path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
               </svg>
              Group
             </span>
             <span
               v-if="screen.group_name"
               class="px-2 py-0.5 rounded text-xs font-medium inline-block"
               :class="getGroupColor(screen.group_name)"
             >{{ screen.group_name.replace(/dkilo /i, '') }}</span>
             <span v-else class="text-gray-500 text-sm">N/A</span>
          </div>
        </div>

        <!-- Driver & Mobile Row -->
        <div class="grid grid-cols-2 gap-4 mb-3 pb-3 border-b border-gray-100">
          <div>
            <span class="text-sm font-medium text-gray-500 flex items-center mb-1">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              Cruiser
            </span>
            <span class="text-gray-800 text-sm truncate block" :title="formatUserName(screen.user_name)">{{ formatUserName(screen.user_name) }}</span>
          </div>
          <div>
            <span class="text-sm font-medium text-gray-500 flex items-center mb-1">
               <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                 <path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
               </svg>
              Mobile
            </span>
            <div class="flex items-center justify-between">
              <span v-if="screen.mobile" class="text-blue-600 text-sm truncate block mr-2" :title="screen.mobile">{{ screen.mobile }}</span>
              <span v-else class="text-gray-500 text-sm">N/A</span>
              <!-- Copy Button for Mobile -->
              <button
                v-if="screen.mobile"
                @click="copyToClipboard(screen.mobile, screen.mobile + '-mobile-btn')"
                :title="copiedItemId === screen.mobile + '-mobile-btn' ? 'Copied!' : 'Copy Mobile'"
                class="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <svg v-if="copiedItemId === screen.mobile + '-mobile-btn'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                   <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                 </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Time Info Row -->
        <div class="grid grid-cols-2 gap-4">
           <div>
             <span class="text-sm font-medium text-gray-500 flex items-center mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                   <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                 </svg>
               Daily Online
             </span>
             <span class="text-gray-800 text-sm">{{ formatTime(screen.daily_online_time) }}</span>
           </div>
           <div>
             <span class="text-sm font-medium text-gray-500 flex items-center mb-1">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                   <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                 </svg>
               Last Seen
             </span>
             <span class="text-gray-800 text-sm">{{ screen.last_offline ? formatRelativeTime(screen.last_offline) : 'Never' }}</span>
           </div>
        </div>

      </div>
    </div>

  </div>
</template>

<script>
import axios from "axios";

export default {
  data() {
    return {
      Screens: [],
      loading: false,
      apiError: false,
      copiedItemId: null,
      sortColumn: 'name', // Default sort column
      sortDirection: 'desc', // Default sort direction
      searchQuery: '', // Added for search input
      isSortMenuOpen: false, // To control custom dropdown visibility
      sortOptions: [
        { value: 'name', text: 'Name' },
        { value: 'screen_id', text: 'Serial' },
        { value: 'group_name', text: 'Group' },
        { value: 'user_name', text: 'Cruiser' },
        { value: 'mobile', text: 'Mobile' },
        { value: 'plate_number', text: 'Plate' },
        { value: 'online_status', text: 'Status' },
        { value: 'daily_online_time', text: 'Online Time' },
        { value: 'last_offline', text: 'Last Seen' },
      ],
    };
  },
  computed: {
    onlineCount() {
        // Filter screens where online_status is 'Online' (case-insensitive)
        return this.Screens.filter(screen => screen.online_status?.toLowerCase() === 'online').length;
    },
    offlineCount() {
        // Calculate offline count based on total and online count
        return this.Screens.length - this.onlineCount;
    },
    sortedScreens() {
      if (!this.Screens) return [];

      // Filter based on search query first
      let filteredScreens = this.Screens;
      if (this.searchQuery) {
        const lowerQuery = this.searchQuery.toLowerCase();
        filteredScreens = this.Screens.filter(screen => {
          return (
            screen.name?.toLowerCase().includes(lowerQuery) ||
            screen.screen_id?.toLowerCase().includes(lowerQuery) ||
            screen.group_name?.toLowerCase().includes(lowerQuery) ||
            screen.user_name?.toLowerCase().includes(lowerQuery) ||
            screen.mobile?.toLowerCase().includes(lowerQuery) ||
            screen.plate_number?.toLowerCase().includes(lowerQuery)
          );
        });
      }

      // Now sort the filtered results
      const screensCopy = [...filteredScreens]; // Use filtered data

      return screensCopy.sort((a, b) => {
        let valA = a[this.sortColumn];
        let valB = b[this.sortColumn];
        let comparison = 0;

        if (this.sortColumn === 'name') {
            const numA = this.extractNumber(a.name);
            const numB = this.extractNumber(b.name);
            comparison = numA - numB;
        } else {
            // Handle potential null/undefined values - treat them as lowest for other columns
            if (valA == null) return -1;
            if (valB == null) return 1;

            // Normalize string values for comparison
            if (typeof valA === 'string') {
                valA = valA.toLowerCase();
            }
            if (typeof valB === 'string') {
                valB = valB.toLowerCase();
            }

            // Standard comparison for other columns
            if (valA > valB) {
                comparison = 1;
            } else if (valA < valB) {
                comparison = -1;
            }
        }

        return this.sortDirection === 'desc' ? comparison * -1 : comparison;
      });
    },
    currentSortOptionText() {
      const selected = this.sortOptions.find(opt => opt.value === this.sortColumn);
      return selected ? selected.text : 'Sort';
    }
  },
  mounted() {
    this.fetchScreensData(); // Fetch data when component is mounted
  },
  methods: {
    async fetchScreensData() {
      this.loading = true;
      this.apiError = false;

      try {
        let response = await axios.get(
          "https://publish.dkilo.com/api/screen-statuses",
          {}
        );

        if (response.status === 200) {
          this.Screens = response.data;
        } else {
          this.apiError = true;
        }
      } catch (error) {
        console.error("Error fetching Screens:", error);
        this.apiError = true;
      } finally {
        this.loading = false;
      }
    },
    formatTime(seconds) {
      if (!seconds || seconds < 0) return "-"; // Default to

      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      // Handle case where total time is less than a minute but not zero
      if (hours === 0 && minutes === 0 && seconds > 0) return `< 1m`;

      let parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`); // Only show minutes if > 0 or if hours is 0
      else if (hours === 0 && minutes === 0) return "-"; // Explicitly return - if both are 0

      return parts.join(" ");
    },
    copyToClipboard(text, id) {
      if (!text) return; // Don't copy if text is null or empty
      navigator.clipboard.writeText(text).then(() => {
        console.log('Copied to clipboard:', text, 'with id:', id);
        // Set the ID of the copied item
        this.copiedItemId = id; // Use renamed variable
        // Reset after a short delay
        setTimeout(() => {
          // Only reset if the current copied ID is the one we set
          if (this.copiedItemId === id) {
            this.copiedItemId = null;
          }
        }, 1500);
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    },
    formatUserName(name) {
        if (!name) return '-';
        const parts = name.trim().split(/\s+/);
        if (parts.length === 0) return '-';
        // Capitalize first letter of first two parts
        const formattedParts = parts.slice(0, 2).map(part =>
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
        );
        return formattedParts.join(' ');
    },
    formatPlateNumber(plate) {
      if (!plate) return '-';
      const plateStr = String(plate);

      // Try to extract number part and text part
      const match = plateStr.match(/^(\d+)\s*([a-zA-Z]+)$|^([a-zA-Z]+)\s*(\d+)$/);

      let numberPart = '-';
      let textPart = '-';

      if (match) {
        if (match[1] && match[2]) { // Format: 1234 ABC
          numberPart = match[1];
          textPart = match[2].toUpperCase();
        } else if (match[3] && match[4]) { // Format: ABC 1234
          numberPart = match[4];
          textPart = match[3].toUpperCase();
        }
        return `${numberPart} ${textPart}`;
      } else {
          // Handle cases that don't fit the expected pattern (e.g., only numbers, only text, or mixed)
          // Attempt to separate numbers and letters regardless of order/spacing
          const numbers = plateStr.replace(/[^0-9]/g, '');
          const letters = plateStr.replace(/[^a-zA-Z]/g, '').toUpperCase();
          if (numbers && letters) {
              return `${numbers} ${letters}`;
          } else if (numbers) {
              return numbers; // Or return '-' if only numbers isn't desired
          } else if (letters) {
              return letters; // Or return '-' if only letters isn't desired
          }
      }

      // Fallback if no pattern matched or extraction failed
      return plateStr.toUpperCase(); // Or return '-'
    },
    formatRelativeTime(timestamp) {
      if (!timestamp) return '-'; // Handle null or empty timestamps
      const now = new Date();
      // Add T separator for better cross-browser date parsing
      const past = new Date(timestamp.replace(' ', 'T'));
      const diffInSeconds = Math.round((now - past) / 1000);

      if (isNaN(diffInSeconds) || diffInSeconds < 0) {
          // Handle invalid date or future date gracefully
          console.warn("Invalid timestamp for relative time:", timestamp);
          return '-'; // Or return the original timestamp
      }

      const minutes = Math.round(diffInSeconds / 60);
      const hours = Math.round(diffInSeconds / 3600);
      const days = Math.round(diffInSeconds / 86400);

      if (diffInSeconds < 60) {
        return 'just now';
      } else if (minutes < 60) {
        return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      } else if (hours < 24) {
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else if (days === 1) {
        return 'yesterday';
      } else {
        return `${days} days ago`;
      }
    },
    sortBy(columnKey) {
      if (this.sortColumn === columnKey) {
        // Toggle direction if clicking the same column
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        // Reset to DESCENDING if clicking a new column
        this.sortColumn = columnKey;
        this.sortDirection = 'desc'; // Changed default to desc
      }
    },
    toggleSortDirection() {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    },
    selectSortOption(value) {
      if (this.sortColumn === value) {
        // Clicked the same option, toggle direction
        this.toggleSortDirection();
      } else {
        // Clicked a new option, set column and default direction
        this.sortColumn = value;
        this.sortDirection = 'desc';
      }
      this.isSortMenuOpen = false; // Close menu
    },
    getGroupColor(groupName) {
      if (!groupName) return 'bg-gray-100 text-gray-700'; // Default
      const colors = [
        'bg-blue-100 text-blue-800',
        'bg-purple-100 text-purple-800',
        'bg-pink-100 text-pink-800',
        'bg-orange-100 text-orange-800',
        'bg-indigo-100 text-indigo-800',
        'bg-cyan-100 text-cyan-800',
        'bg-lime-100 text-lime-800',
        'bg-red-100 text-red-800',      // Added Red
        'bg-yellow-100 text-yellow-800', // Added Yellow
        'bg-green-100 text-green-800',  // Added Green
        'bg-teal-100 text-teal-800',    // Added Teal
      ];
      // Simple hash function to pick a color based on name
      let hash = 0;
      for (let i = 0; i < groupName.length; i++) {
        hash = groupName.charCodeAt(i) + ((hash << 5) - hash);
      }
      const index = Math.abs(hash) % colors.length;
      return colors[index];
    },
    truncateSerial(serial) {
      if (!serial) return '-';
      const maxLength = 12; // Adjust number of characters to show
      if (serial.length <= maxLength) {
        return serial;
      }
      return serial.substring(0, maxLength) + '...';
    },
    extractNumber(str) {
        if (!str) return Infinity; // Treat null/empty as highest for sorting purposes if needed
        const match = str.match(/-?(\d+)$/); // Match number at the end
        return match ? parseInt(match[1], 10) : Infinity; // Return number or Infinity if no match
    },
  },
};
</script>

<style scoped>
.table-container {
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}

/* Fade transition for 'Copied!' notification - Only Fade Out */
.fade-leave-active {
  transition: opacity 1.5s ease;
}

.fade-leave-to {
  opacity: 0;
}

/* Fade transition for sort dropdown menu */
.sort-menu-fade-enter-active,
.sort-menu-fade-leave-active {
  transition: opacity 0.2s ease;
}

.sort-menu-fade-enter-from,
.sort-menu-fade-leave-to {
  opacity: 0;
}

/* Removed th, td specific styles as they are now handled by Tailwind */
</style>
